import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../mockData_extended';
import { Globe, ChevronDown, Menu, X } from 'lucide-react';
import { trackLanguageChange } from '../utils/analytics';

const Header = () => {
  const { language, changeLanguage, isOpen, toggleDropdown, setIsOpen } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Wrap changeLanguage to track analytics
  const handleLanguageChange = (langCode) => {
    trackLanguageChange(langCode);
    changeLanguage(langCode);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  // Close mobile menu on scroll
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const navItems = [
    { id: 'overview', en: 'Overview', gr: 'Επισκόπηση' },
    { id: 'specifications', en: 'Specifications', gr: 'Προδιαγραφές' },
    { id: 'gallery', en: 'Gallery', gr: 'Φωτογραφίες' },
    { id: 'location', en: 'Location', gr: 'Τοποθεσία' },
    { id: 'contact', en: 'Contact', gr: 'Επικοινωνία' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-white/80 backdrop-blur-xl shadow-2xl' 
            : 'bg-gradient-to-b from-black/30 to-transparent backdrop-blur-sm'
        }`}
        style={{
          boxShadow: scrolled ? '0 10px 40px rgba(0,0,0,0.1)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className={`text-lg sm:text-xl font-semibold transition-colors duration-300 ${
            scrolled ? 'text-slate-800' : 'text-white'
          }`}>
            Skopelos Property
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`transition-all duration-300 hover:scale-110 text-sm xl:text-base ${
                  scrolled ? 'text-slate-700 hover:text-[#d4845f]' : 'text-white hover:text-[#d4845f]'
                }`}
              >
                {language === 'en' ? item.en : item.gr}
              </button>
            ))}
          </nav>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleDropdown}
                className={`flex items-center gap-1 sm:gap-2 transition-all duration-300 text-xs sm:text-sm px-2 sm:px-3 ${
                  scrolled 
                    ? 'border-slate-300 hover:border-[#d4845f] hover:text-[#d4845f]' 
                    : 'border-white/50 text-white hover:border-[#d4845f] hover:text-[#d4845f] hover:bg-white/10'
                }`}
              >
                <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
                <span className="sm:hidden">{currentLanguage.flag}</span>
                <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
              </Button>

              {/* Dropdown Menu */}
              {isOpen && (
                <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-2xl border border-slate-200 py-2 z-50 max-h-80 overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 hover:bg-slate-50 transition-colors flex items-center gap-2 sm:gap-3 text-sm sm:text-base ${
                        language === lang.code ? 'bg-slate-100 text-[#d4845f] font-semibold' : 'text-slate-700'
                      }`}
                    >
                      <span className="text-lg sm:text-xl">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                scrolled ? 'text-slate-800 hover:bg-slate-100' : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <nav 
            className="absolute top-16 left-4 right-4 bg-white rounded-2xl shadow-2xl p-6 animate-in fade-in slide-in-from-top-4 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="w-full text-left px-4 py-3 text-slate-700 hover:text-[#d4845f] hover:bg-slate-50 rounded-lg transition-colors text-lg font-medium"
                >
                  {language === 'en' ? item.en : item.gr}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
